import PropTypes from "prop-types"
import Image from "components/ui/Image"

/**
 * ImageWrapper - A flexible media container component for images and videos
 *
 * Usage Examples:
 *
 * 1. Single image (backward compatible):
 * <ImageWrapper image="https://example.com/image.png" />
 *
 * 2. Single image with width:
 * <ImageWrapper image="https://example.com/image.png" width="400px" />
 *
 * 3. Single image with footnote:
 * <ImageWrapper
 *   image="https://example.com/image.png"
 *   footnote="This is a caption for the image"
 * />
 *
 * 4. Single video:
 * <ImageWrapper image="https://example.com/video.mp4" />
 *
 * 5. Multiple images/videos (responsive: stacked on mobile, row on desktop):
 * <ImageWrapper
 *   images={[
 *     { src: "https://example.com/image1.png" },
 *     { src: "https://example.com/video.mp4" },
 *     { src: "https://example.com/image3.png" }
 *   ]}
 * />
 *
 * 6. Multiple media with footnotes:
 * <ImageWrapper
 *   images={[
 *     { src: "https://example.com/image1.png", footnote: "First image caption" },
 *     { src: "https://example.com/video.mp4", footnote: "Demo video" },
 *     { src: "https://example.com/image3.png" }  // This one has no footnote
 *   ]}
 * />
 *
 * 7. Multiple media with individual widths:
 * <ImageWrapper
 *   images={[
 *     { src: "https://example.com/image1.png", width: "300px", footnote: "Smaller image" },
 *     { src: "https://example.com/video.mp4", width: "500px", footnote: "Larger video" }
 *   ]}
 * />
 *
 * 8. Multiple media (MDX-friendly, responsive):
 * <imagewrapper
 *   image1="https://example.com/image1.png"
 *   image2="https://example.com/video.mp4"
 *   image3="https://example.com/image3.png"
 * />
 *
 * 9. Multiple media with footnotes (MDX-friendly):
 * <imagewrapper
 *   image1="https://example.com/image1.png" footnote1="First caption"
 *   image2="https://example.com/video.mp4" footnote2="Video demo"
 *   image3="https://example.com/image3.png" footnote3="Third caption"
 * />
 */
function ImageWrapper({
  image,
  width,
  footnote,
  images,
  image1,
  image2,
  image3,
  footnote1,
  footnote2,
  footnote3,
}) {
  // Support multiple input formats for MDX compatibility
  let imageArray = []

  try {
    if (images && Array.isArray(images)) {
      // Standard array format (works in JSX)
      imageArray = images
    } else if (image1 || image2 || image3) {
      // Alternative format for MDX: image1="url" image2="url" footnote1="text"
      imageArray = []
      if (image1) imageArray.push({ src: image1, footnote: footnote1 })
      if (image2) imageArray.push({ src: image2, footnote: footnote2 })
      if (image3) imageArray.push({ src: image3, footnote: footnote3 })
    } else if (image) {
      // Single image format (backward compatibility)
      imageArray = [{ src: image, width, footnote }]
    }

    if (!imageArray.length) return null
  } catch (_error) {
    return (
      <div className="p-4 border border-red-300 bg-red-50">
        Error loading media
      </div>
    )
  }

  const hasMultipleImages = imageArray.length > 1

  // Helper function to check if a source is a video
  const isVideo = (src) => {
    if (!src) return false
    const url = typeof src === "string" ? src : src?.url || ""
    return url.toLowerCase().includes(".mp4")
  }

  // Helper function to get the source URL
  const getSrcUrl = (src) => {
    if (typeof src === "string") return src
    return src?.url || ""
  }

  // Use span to avoid DOM nesting issues with MDX, but style as flex
  return (
    <span
      className={`p-10 shadow-simple flex items-center ${
        hasMultipleImages ? "flex-col md:flex-row gap-4" : "justify-center"
      }`}
      style={{
        display: "flex !important",
        margin: "1rem 0",
      }}
    >
      {imageArray.map((img, index) => {
        const isVideoFile = isVideo(img.src)
        const srcUrl = getSrcUrl(img.src)
        // Use index to ensure unique keys even with duplicate URLs
        const uniqueKey = `${index}-${srcUrl ? srcUrl.slice(-20) : "empty"}`

        const mediaElement = isVideoFile ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video
            key={uniqueKey}
            className="max-w-full shadow-simple mx-auto"
            src={srcUrl}
            controls
            style={{
              ...(img.width
                ? { width: img.width }
                : width
                  ? { width }
                  : undefined),
              display: "block",
              margin: img.width || width ? "0 auto" : 0,
            }}
          />
        ) : (
          <Image
            key={uniqueKey}
            className="max-w-full shadow-simple mx-auto"
            src={img.src}
            style={{
              ...(img.width
                ? { width: img.width }
                : width
                  ? { width }
                  : undefined),
              display: "block",
              margin: img.width || width ? "0 auto" : 0,
            }}
          />
        )

        if (img.footnote || (imageArray.length === 1 ? footnote : null)) {
          return (
            <span
              key={`media-${uniqueKey}`}
              className={hasMultipleImages ? "md:flex-1" : ""}
              style={{ margin: 0, display: "block", textAlign: "center" }}
            >
              {mediaElement}
              <span
                className="text-sm text-gray-600 mt-2 text-center italic"
                style={{ display: "block" }}
              >
                {img.footnote || (imageArray.length === 1 ? footnote : null)}
              </span>
            </span>
          )
        }

        return hasMultipleImages ? (
          <span
            key={`div-${uniqueKey}`}
            className="md:flex-1"
            style={{ display: "block" }}
          >
            {mediaElement}
          </span>
        ) : (
          <span key={`div-${uniqueKey}`} style={{ display: "block" }}>
            {mediaElement}
          </span>
        )
      })}
    </span>
  )
}

ImageWrapper.propTypes = {
  /** Single image source (for backward compatibility) */
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  /** Image Width (applies to single image or all images if not specified per image) */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Footnote for single image */
  footnote: PropTypes.string,
  /** Array of images with optional per-image footnotes and widths */
  images: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})])
        .isRequired,
      footnote: PropTypes.string,
      width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  /** MDX-friendly multiple image props */
  image1: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  image2: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  image3: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  footnote1: PropTypes.string,
  footnote2: PropTypes.string,
  footnote3: PropTypes.string,
}

export default ImageWrapper
